<template>
  <div class="relative flex min-h-screen w-full flex-col items-center justify-center p-4 bg-base dark:bg-background-dark">
    <div class="flex w-full max-w-lg flex-col overflow-hidden rounded-xl border border-line dark:border-roundBorder bg-surface shadow-lg">
      <!-- Header -->
      <header class="flex items-center justify-between whitespace-nowrap border-b border-content/10 px-6 py-4">
        <div class="flex items-center gap-3 text-content">
          <div class="size-6 text-primary">
            <svg fill="none" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
              <g clip-path="url(#clip0_6_330)">
                <path clip-rule="evenodd" d="M24 0.757355L47.2426 24L24 47.2426L0.757355 24L24 0.757355ZM21 35.7574V12.2426L9.24264 24L21 35.7574Z" fill="currentColor" fill-rule="evenodd"></path>
              </g>
              <defs>
                <clipPath id="clip0_6_330">
                  <rect fill="white" height="48" width="48"></rect>
                </clipPath>
              </defs>
            </svg>
          </div>
          <h2 class="text-lg font-bold leading-tight tracking-[-0.015em] text-content">{{ $t('login.title') }}</h2>
        </div>
      </header>

      <!-- Loading State -->
      <main v-if="isLoading" class="flex flex-col items-center justify-center gap-4 p-8">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        <p class="text-muted">{{ $t('account.resetPassword.validating') }}</p>
      </main>

      <!-- Invalid Token -->
      <main v-else-if="!isValidToken" class="flex flex-col gap-6 p-6 md:p-8">
        <div class="flex flex-col items-center gap-4 text-center">
          <div class="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center">
            <span class="material-symbols-outlined text-red-400 text-4xl">error</span>
          </div>
          <p class="text-title font-bold leading-tight tracking-[-0.033em] text-content">{{ $t('account.resetPassword.invalidTokenTitle') }}</p>
          <p class="text-title-sub font-normal leading-normal text-muted">{{ $t('account.resetPassword.invalidTokenDesc') }}</p>
          <button
            @click="goToForgotPassword"
            class="mt-4 flex h-12 min-w-[84px] cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-primary px-5 text-grid-btn font-bold leading-normal tracking-[0.015em] text-white transition-colors hover:bg-primary/90"
          >
            {{ $t('account.resetPassword.requestNewLink') }}
          </button>
        </div>
      </main>

      <!-- Reset Success -->
      <main v-else-if="resetSuccess" class="flex flex-col gap-6 p-6 md:p-8">
        <div class="flex flex-col items-center gap-4 text-center">
          <div class="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
            <span class="material-symbols-outlined text-green-400 text-4xl">check_circle</span>
          </div>
          <p class="text-title font-bold leading-tight tracking-[-0.033em] text-content">{{ $t('account.resetPassword.successTitle') }}</p>
          <p class="text-title-sub font-normal leading-normal text-muted">{{ $t('account.resetPassword.successDesc') }}</p>
          <button
            @click="goToLogin"
            class="mt-4 flex h-12 min-w-[84px] cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-primary px-5 text-grid-btn font-bold leading-normal tracking-[0.015em] text-white transition-colors hover:bg-primary/90"
          >
            {{ $t('account.resetPassword.goToLogin') }}
          </button>
        </div>
      </main>

      <!-- Reset Form -->
      <main v-else class="flex flex-col gap-6 p-6 md:p-8">
        <div class="flex flex-col gap-2">
          <p class="text-title font-bold leading-tight tracking-[-0.033em] text-content">{{ $t('account.resetPassword.title') }}</p>
          <p class="text-title-sub font-normal leading-normal text-muted">{{ $t('account.resetPassword.description') }}</p>
        </div>

        <!-- Form -->
        <form @submit.prevent="handleSubmit" class="flex flex-col gap-4">
          <label class="flex flex-col w-full">
            <p class="pb-2 text-form-label leading-normal text-content">{{ $t('account.info.password') }}</p>
            <input
              v-model="password"
              type="password"
              required
              :class="[
                'form-input h-12 w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg border dark:border-roundBorder bg-base p-3 text-base font-normal leading-normal text-content placeholder:text-muted focus:outline-0 focus:ring-2 focus:ring-primary/50',
                passwordError ? 'border-red-500 focus:border-red-500' : 'border-content/20 focus:border-primary'
              ]"
              :placeholder="$t('account.info.passwordPlaceholder')"
            />
            <p v-if="passwordError" class="mt-1 text-sm text-red-400">{{ passwordError }}</p>
            <p v-else class="mt-1 text-xs text-muted">{{ $t('account.info.passwordInvalid') }}</p>
          </label>

          <label class="flex flex-col w-full">
            <p class="pb-2 text-form-label leading-normal text-content">{{ $t('account.info.confirmPassword') }}</p>
            <input
              v-model="confirmPassword"
              type="password"
              required
              :class="[
                'form-input h-12 w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg border dark:border-roundBorder bg-base p-3 text-base font-normal leading-normal text-content placeholder:text-muted focus:outline-0 focus:ring-2 focus:ring-primary/50',
                confirmPasswordError ? 'border-red-500 focus:border-red-500' : 'border-content/20 focus:border-primary'
              ]"
              :placeholder="$t('account.info.confirmPasswordPlaceholder')"
            />
            <p v-if="confirmPasswordError" class="mt-1 text-sm text-red-400">{{ confirmPasswordError }}</p>
          </label>

          <button
            type="submit"
            :disabled="isSubmitting"
            class="flex h-12 min-w-[84px] cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-primary px-5 text-grid-btn font-bold leading-normal tracking-[0.015em] text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span class="truncate">{{ isSubmitting ? $t('account.resetPassword.resetting') : $t('account.resetPassword.resetButton') }}</span>
          </button>

          <!-- Error Message -->
          <div v-if="errorMessage" class="rounded-lg bg-red-500/20 border border-red-500/50 p-3 text-sm text-red-400">
            {{ errorMessage }}
          </div>
        </form>
      </main>

      <!-- Footer -->
      <footer class="border-t border-solid border-content/10 bg-content/5 px-6 py-4">
        <span
          @click="goToLogin"
          class="block text-center text-title-sub-xs leading-normal text-muted underline hover:text-content transition-colors cursor-pointer"
        >
          {{ $t('account.passwordReset.backToLogin') }}
        </span>
      </footer>
    </div>
  </div>
</template>

<script>
  import { useI18n } from 'vue-i18n';
  import axios from 'axios';
  import { AUTH_URL } from '@/config';

  export default {
    name: 'ResetPassword',
    data() {
      return {
        token: '',
        email: '',
        password: '',
        confirmPassword: '',
        passwordError: '',
        confirmPasswordError: '',
        errorMessage: '',
        isLoading: true,
        isValidToken: false,
        isSubmitting: false,
        resetSuccess: false
      };
    },
    setup() {
      const { t } = useI18n();
      return { t };
    },
    async created() {
      this.token = this.$route.query.token;
      if (!this.token) {
        this.isLoading = false;
        this.isValidToken = false;
        return;
      }

      // Validate token
      try {
        const response = await axios.get(`${AUTH_URL}/validateResetToken`, {
          params: { token: this.token }
        });

        if (response.data.valid) {
          this.isValidToken = true;
          this.email = response.data.email;
        } else {
          this.isValidToken = false;
        }
      } catch (error) {
        console.error('Token validation error:', error);
        this.isValidToken = false;
      } finally {
        this.isLoading = false;
      }
    },
    methods: {
      goToLogin() {
        if (window.Android && typeof window.Android.onResetPasswordComplete === 'function') {
          window.Android.onResetPasswordComplete();
        } else {
          this.$router.push('/login');
        }
      },
      goToForgotPassword() {
        this.$router.push('/forgot-password');
      },
      validatePassword() {
        // Password: 8-20 chars, letters, numbers, special characters
        const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,20}$/;
        if (!passwordRegex.test(this.password)) {
          this.passwordError = this.$t('account.info.passwordInvalid');
          return false;
        }
        this.passwordError = '';
        return true;
      },
      validateConfirmPassword() {
        if (this.password !== this.confirmPassword) {
          this.confirmPasswordError = this.$t('account.info.passwordNotMatch');
          return false;
        }
        this.confirmPasswordError = '';
        return true;
      },
      async handleSubmit() {
        this.errorMessage = '';

        // Validate
        const isPasswordValid = this.validatePassword();
        const isConfirmValid = this.validateConfirmPassword();

        if (!isPasswordValid || !isConfirmValid) {
          return;
        }

        this.isSubmitting = true;

        try {
          const response = await axios.post(`${AUTH_URL}/resetPassword`, {
            token: this.token,
            password: this.password
          });

          if (response.data.success) {
            this.resetSuccess = true;
          } else {
            this.errorMessage = this.$t('account.resetPassword.error');
          }
        } catch (error) {
          console.error('Password reset error:', error);
          if (error.response && error.response.status === 400) {
            this.errorMessage = this.$t('account.resetPassword.invalidTokenDesc');
          } else {
            this.errorMessage = this.$t('account.resetPassword.error');
          }
        } finally {
          this.isSubmitting = false;
        }
      }
    }
  };
</script>

<style scoped>
  /* Override browser autofill styles */
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 30px rgb(var(--c-surface)) inset !important;
    -webkit-text-fill-color: white !important;
    caret-color: white !important;
    transition: background-color 5000s ease-in-out 0s;
  }
</style>
